import { useEffect, useRef, useState } from 'react';
import { fr } from 'date-fns/locale';
import DatePicker, { registerLocale } from 'react-datepicker';
import { toast } from 'react-toastify';
import {
  accountStatementErrorText,
  accountStatementFileName,
  accountStatementMailErrorText,
  startOfCurrentYear,
  startOfToday,
  toIsoDay,
} from 'components/accountStatement/accountStatementFile';
import Button from 'components/button';
import { DownloadIcon } from 'components/invoiceDetail/InvoicePdfPreview';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import { downloadFile } from 'components/invoiceList/useInvoiceExport';
import MailRecipientList from 'components/invoiceSendMail/MailRecipientList';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import StyledDateInput from 'components/styledInput/StyledDateInput';
import Tooltip from 'components/tooltips/Tooltips';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

registerLocale('fr-FR', fr);

const refKeyOf = (contact) => `${contact.ref.kind}:${contact.ref.id}`;

/**
 * The PDF account statement of a company over a chosen period, built by the
 * api from its customer account at Pennylane. `modalData` is
 * `{ idCompany, companyName }`; the name titles the contacts and names the
 * file.
 *
 * The main action mails the statement to the company's billing contacts
 * ticked in the modal, the list of the invoice mail; the icon on the left
 * downloads it. A refusal stays in the modal with the text of its code; a
 * success closes it.
 */
const ModalAccountStatement = () => {
  const { toggle } = useModal();
  const { language, modalData } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`accountStatement.${key}`, fallback);
  const [from, setFrom] = useState(startOfCurrentYear);
  const [to, setTo] = useState(startOfToday);
  // "download" or "mail" while the api answers.
  const [pending, setPending] = useState(null);
  const [failure, setFailure] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [contactsFailed, setContactsFailed] = useState(false);
  const [contactsKey, setContactsKey] = useState(0);
  // The admin's ticks by contact; any other contact keeps the api's
  // `preselected` ("Factures" ticked).
  const [ticks, setTicks] = useState({});
  // State lands one render late: a double click must still send one request.
  const isSending = useRef(false);

  const idCompany = modalData?.idCompany;

  useEffect(() => {
    if (!idCompany) return undefined;
    let active = true;
    setContactsFailed(false);
    ApiDatabase.getCompanyAccountStatementRecipients(
      { idCompany },
      (response) => {
        if (!active) return;
        const listed = response?.data?.contacts;
        setContacts(Array.isArray(listed) ? listed : []);
      },
      (error) => {
        if (!active || error?.name === 'CanceledError') return;
        setContactsFailed(true);
      },
    );
    return () => {
      active = false;
    };
  }, [idCompany, contactsKey]);

  if (!idCompany) return null;

  const fromDay = from ? toIsoDay(from) : null;
  const toDay = to ? toIsoDay(to) : null;
  // Same-length "YYYY-MM-DD" strings compare as dates, whatever the time of day
  // the picker put on them.
  const isPeriodValid = Boolean(fromDay && toDay) && fromDay <= toDay;
  const isPending = pending !== null;
  const isTicked = (contact) => {
    const key = refKeyOf(contact);
    return key in ticks ? ticks[key] : contact.preselected === true;
  };
  const tickedContacts = (contacts ?? []).filter(isTicked);
  const isDownloadDisabled = isPending || !isPeriodValid;
  const isMailDisabled =
    isPending || !isPeriodValid || tickedContacts.length === 0;

  const toggleContact = (contact) => {
    if (isPending) return;
    setFailure(null);
    const key = refKeyOf(contact);
    const ticked = isTicked(contact);
    setTicks((current) => ({ ...current, [key]: !ticked }));
  };

  const start = (action) => {
    isSending.current = true;
    setPending(action);
    setFailure(null);
  };

  const done = () => {
    isSending.current = false;
    setPending(null);
  };

  const download = () => {
    if (isSending.current || !isPeriodValid) return;
    start('download');
    ApiDatabase.getCompanyAccountStatement(
      { idCompany, from: fromDay, to: toDay },
      (blob) => {
        done();
        downloadFile(
          blob,
          accountStatementFileName(modalData?.companyName, fromDay, toDay),
        );
        toggle();
      },
      (error) => {
        done();
        if (error?.name === 'CanceledError') return;
        // Never the error object in the console: axios keeps the request, token included.
        setFailure(
          accountStatementErrorText(language, error?.response?.data?.errorCode),
        );
      },
    );
  };

  const sendMail = () => {
    if (isSending.current || isMailDisabled) return;
    start('mail');
    ApiDatabase.postCompanyAccountStatementMail(
      {
        idCompany,
        from: fromDay,
        to: toDay,
        // References only: the api finds the addresses itself.
        recipients: tickedContacts.map(({ ref }) => ({
          kind: ref.kind,
          id: ref.id,
        })),
      },
      (response) => {
        done();
        const count = response?.data?.recipientCount ?? tickedContacts.length;
        // Outside production the api diverts the mail to its test address:
        // the contacts ticked never receive it, and the toast must not say so.
        if (response?.data?.outcome === 'redirected') {
          toast.success(
            text(
              'sentRedirected',
              'Relevé de compte envoyé à l’adresse de test : les contacts ne l’ont pas reçu.',
            ),
          );
          toggle();
          return;
        }
        toast.success(
          fillText(
            count > 1
              ? text(
                  'sentMany',
                  'Relevé de compte envoyé à {count} destinataires',
                )
              : text(
                  'sentOne',
                  'Relevé de compte envoyé à {count} destinataire',
                ),
            { count },
          ),
        );
        toggle();
      },
      (error) => {
        done();
        if (error?.name === 'CanceledError') return;
        setFailure(accountStatementMailErrorText(language, error));
      },
    );
  };

  const datePickerProps = {
    dateFormat: 'dd/MM/yyyy',
    locale: 'fr-FR',
    placeholderText: text('datePlaceholder', 'jj/mm/aaaa'),
    disabled: isPending,
    showMonthDropdown: true,
    showYearDropdown: true,
    dropdownMode: 'select',
    popperPlacement: 'bottom',
    // ModalBody scrolls: an absolute calendar would be clipped at its bottom
    // edge, under the footer.
    popperProps: { strategy: 'fixed' },
  };

  let recipients;
  if (contactsFailed) {
    recipients = (
      <div
        role="alert"
        className="flex items-center justify-between gap-3 rounded-lg bg-red-50 px-4 py-3 text-red-700"
      >
        <span>
          {text(
            'recipientsLoadFailed',
            'Les contacts de facturation n’ont pas pu être chargés.',
          )}
        </span>
        <Button
          color="SECONDARY"
          onClick={() => setContactsKey((key) => key + 1)}
        >
          {text('recipientsRetry', 'Réessayer')}
        </Button>
      </div>
    );
  } else if (!contacts) {
    recipients = (
      <div role="status" className="py-3 text-center text-gray-500">
        {text('recipientsLoading', 'Chargement des contacts de facturation…')}
      </div>
    );
  } else {
    recipients = (
      <MailRecipientList
        companyName={modalData?.companyName ?? ''}
        contacts={contacts}
        isTicked={isTicked}
        onToggle={toggleContact}
        disabled={isPending}
      />
    );
  }

  const downloadLabel = text('download', 'Télécharger le PDF');

  return (
    <>
      <Modalheader hide={toggle} hideShow={!isPending}>
        {text('title', 'Relevé de compte')}
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4 text-sm">
          <p className="text-gray-700">
            {text(
              'intro',
              'Les mouvements du compte client de l’entreprise chez Pennylane sur la période, à partir du solde au premier jour.',
            )}
          </p>
          {/* One cell per picker: an open calendar adds a sibling to its
              input, which would otherwise push « Au » to the next row. */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <DatePicker
                {...datePickerProps}
                selected={from}
                onChange={(date) => setFrom(date)}
                customInput={<StyledDateInput label={text('from', 'Du')} />}
              />
            </div>
            <div>
              <DatePicker
                {...datePickerProps}
                selected={to}
                onChange={(date) => setTo(date)}
                customInput={<StyledDateInput label={text('to', 'Au')} />}
              />
            </div>
          </div>
          {!isPeriodValid && (
            <div role="alert" className="text-red-600">
              {accountStatementErrorText(language, 'INVALID_PERIOD')}
            </div>
          )}
          {recipients}
          {failure && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 px-4 py-3 text-red-700"
            >
              {failure}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {/* The download stands apart, on the left: the mail is the main action. */}
        <div className="mr-auto">
          <Tooltip text={downloadLabel} placement="top">
            <Button
              size="ICON_LG"
              color="SECONDARY"
              aria-label={downloadLabel}
              aria-busy={pending === 'download' || undefined}
              disabled={isDownloadDisabled}
              className="disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={download}
            >
              <DownloadIcon />
            </Button>
          </Tooltip>
        </div>
        <Button
          size="LG"
          color={isPending ? 'DISABLED' : 'SECONDARY'}
          disabled={isPending}
          onClick={toggle}
        >
          {text('cancel', 'Annuler')}
        </Button>
        <Button
          size="LG"
          color={isMailDisabled ? 'DISABLED' : 'PRIMARY'}
          disabled={isMailDisabled}
          onClick={sendMail}
        >
          {pending === 'mail'
            ? text('sending', 'Envoi en cours…')
            : text('sendMail', 'Envoyer par mail')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalAccountStatement;
